import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Flex, Input, Select, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';

import { ReactComponent as SearchIcon } from '../../../assets/magnifier.svg';
import Button from '../../../custom/button/button';
import { useDebounce } from '../../../hooks/useDebounce';
import { useDownload } from '../../../hooks/useDownload';
import { usePagination } from '../../../hooks/usePagination';
import { getAllApplicationBatch } from '../../../requests';
import api from '../../../utils/api';
import { buildQuery } from '../../../utils/buildQuery';
import { formatFullDate } from '../../../utils/formatDate';
import styles from '../../setup/styles.module.scss';

interface ScholarshipRecord {
  id: number;
  applicantId: string;
  applicationNumber: string;
  applicantName: string;
  originalFileName: string;
  filePath: string;
  status: number;
  statusLabel: string;
  submittedAt: string;
  scholarshipPercent: number;
  acceptanceOriginalFileName: string | null;
  batchName: string;
  programme: string;
  hasUploadedAcceptance: boolean;
}

interface ScholarshipFilters {
  ApplicationNumber: string;
  ApplicantName: string;
  Status: number | '';
  ApplicationBatchId: number | '';
  HasUploadedAcceptance: boolean | '';
  ExcludeWorkflowInitiated: boolean | '';
}

const EMPTY_FILTERS: ScholarshipFilters = {
  ApplicationNumber: '',
  ApplicantName: '',
  Status: '',
  ApplicationBatchId: '',
  HasUploadedAcceptance: '',
  ExcludeWorkflowInitiated: '',
};

const STATUS_OPTIONS = [
  { label: 'Pending', value: 1 },
  { label: 'Approved', value: 2 },
  { label: 'Rejected', value: 3 },
];

const STATUS_COLORS: Record<number, string> = {
  1: 'warning',
  2: 'success',
  3: 'error',
};

const getScholarshipReport = async (params: Record<string, any>) => {
  const url = buildQuery(params);
  return (await api.get(`/admin/scholarship${url}`))?.data;
};

const downloadScholarshipReport = async (params: Record<string, any>) => {
  const url = buildQuery(params);
  return (
    await api.get(`/admin/scholarship/download${url}`, { responseType: 'blob' })
  )?.data as Blob;
};

const parseBlobError = async (error: any): Promise<string> => {
  const fallback = 'Unable to download the scholarship report. Please try again.';
  try {
    const blob: Blob = error?.response?.data;
    if (blob instanceof Blob) {
      const text = await blob.text();
      const json = JSON.parse(text);
      return json?.message || fallback;
    }
  } catch {}
  return error?.response?.data?.message || fallback;
};

const ScholarshipReport = () => {
  const { notification } = App.useApp();
  const { currentPage, onChange } = usePagination();
  const { downloadFile, downloadExcelFile, isDownloading } = useDownload();

  const [filters, setFilters] = useState<ScholarshipFilters>(EMPTY_FILTERS);

  const debouncedAppNumber = useDebounce(filters.ApplicationNumber, 500);
  const debouncedApplicantName = useDebounce(filters.ApplicantName, 500);

  const buildParams = () => ({
    Page: currentPage,
    PageSize: 10,
    ApplicationNumber: debouncedAppNumber || undefined,
    ApplicantName: debouncedApplicantName || undefined,
    Status: filters.Status !== '' ? filters.Status : undefined,
    ApplicationBatchId: filters.ApplicationBatchId || undefined,
    HasUploadedAcceptance: filters.HasUploadedAcceptance !== '' ? filters.HasUploadedAcceptance : undefined,
    ExcludeWorkflowInitiated:
      filters.ExcludeWorkflowInitiated !== '' ? filters.ExcludeWorkflowInitiated : undefined,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['scholarship-report', buildParams()],
    queryFn: () => getScholarshipReport(buildParams()),
  });

  const { data: batchData, isLoading: batchLoading } = useQuery({
    queryKey: ['application-batches'],
    queryFn: getAllApplicationBatch,
  });

  const downloadMutation = useMutation({ mutationFn: downloadScholarshipReport });

  const records: ScholarshipRecord[] = data?.data?.data ?? [];
  const totalRecords: number = data?.data?.totalRecords ?? 0;

  const setFilter = (key: keyof ScholarshipFilters, value: any) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const handleClearFilters = () => setFilters(EMPTY_FILTERS);

  const handleDownload = async (downloadAll: boolean) => {
    const activeFilters = {
      ApplicationNumber: debouncedAppNumber || undefined,
      ApplicantName: debouncedApplicantName || undefined,
      Status: filters.Status !== '' ? filters.Status : undefined,
      ApplicationBatchId: filters.ApplicationBatchId || undefined,
      HasUploadedAcceptance:
        filters.HasUploadedAcceptance !== '' ? filters.HasUploadedAcceptance : undefined,
      ExcludeWorkflowInitiated:
        filters.ExcludeWorkflowInitiated !== '' ? filters.ExcludeWorkflowInitiated : undefined,
    };

    const params = {
      Page: 1,
      PageSize: 100000,
      ...(downloadAll ? {} : activeFilters),
    };

    await downloadMutation.mutateAsync(params, {
      onSuccess: (blob) => {
        downloadExcelFile(
          blob,
          downloadAll ? 'Scholarship_Report_All.xlsx' : 'Scholarship_Report_Filtered.xlsx',
        );
      },
      onError: async (error: any) => {
        const description = await parseBlobError(error);
        notification.error({ message: 'Download Failed', description });
      },
    });
  };

  const batchOptions = (batchData?.data as any[])?.map((b: any) => ({
    label: b.batchName,
    value: b.id,
  }));

  const columns: ColumnsType<ScholarshipRecord> = [
    {
      key: 'sn',
      title: 'S/N',
      width: 55,
      render: (_, __, index) => (currentPage - 1) * 10 + index + 1,
    },
    {
      key: 'applicationNumber',
      title: 'App. No.',
      dataIndex: 'applicationNumber',
    },
    {
      key: 'applicantName',
      title: 'Applicant Name',
      dataIndex: 'applicantName',
    },
    {
      key: 'programme',
      title: 'Programme',
      dataIndex: 'programme',
    },
    {
      key: 'batchName',
      title: 'Batch',
      dataIndex: 'batchName',
    },
    {
      key: 'scholarshipPercent',
      title: 'Scholarship %',
      dataIndex: 'scholarshipPercent',
      render: (val: number) => `${val}%`,
    },
    {
      key: 'status',
      title: 'Status',
      render: (_, record) => (
        <Tag color={STATUS_COLORS[record.status] ?? 'default'}>{record.statusLabel}</Tag>
      ),
    },
    {
      key: 'hasUploadedAcceptance',
      title: 'Acceptance Uploaded',
      dataIndex: 'hasUploadedAcceptance',
      render: (val: boolean) => (
        <Tag color={val ? 'success' : 'default'}>{val ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      key: 'submittedAt',
      title: 'Submitted At',
      dataIndex: 'submittedAt',
      render: (val: string) => formatFullDate(val),
    },
    // {
    //   key: 'document',
    //   title: 'Document',
    //   render: (_, record) =>
    //     record.filePath ? (
    //       <Button
    //         text="Download"
    //         variant="text"
    //         onClick={() => downloadFile(record.filePath, record.originalFileName)}
    //         isLoading={isDownloading}
    //         disabled={isDownloading}
    //       />
    //     ) : (
    //       <span style={{ color: '#9ca3af' }}>—</span>
    //     ),
    // },
  ];

  return (
    <div>
      <section className="space-between">
        <h3>Scholarship Report</h3>
        <Flex gap="8px" wrap="wrap">
          <Button
            text="Download Filtered"
            onClick={() => handleDownload(false)}
            isLoading={downloadMutation.isPending}
            disabled={downloadMutation.isPending}
          />
          <Button
            text="Download All"
            onClick={() => handleDownload(true)}
            isLoading={downloadMutation.isPending}
            disabled={downloadMutation.isPending}
            variant="text"
          />
        </Flex>
      </section>

      <section className={styles.card}>
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontWeight: 600, marginBottom: 8, color: '#374151' }}>Filters</p>
          <Flex gap="8px" wrap="wrap" align="center">
            <Input
              placeholder="Application Number"
              value={filters.ApplicationNumber}
              onChange={(e) => setFilter('ApplicationNumber', e.target.value)}
              prefix={<SearchIcon style={{ width: 14, height: 14 }} />}
              style={{ width: 180, borderColor: '#8F0024' }}
              allowClear
            />
            <Input
              placeholder="Applicant Name"
              value={filters.ApplicantName}
              onChange={(e) => setFilter('ApplicantName', e.target.value)}
              prefix={<SearchIcon style={{ width: 14, height: 14 }} />}
              style={{ width: 180, borderColor: '#8F0024' }}
              allowClear
            />
            <Select
              placeholder="Status"
              value={filters.Status !== '' ? filters.Status : undefined}
              allowClear
              options={STATUS_OPTIONS}
              style={{ width: 150, borderColor: '#8F0024' }}
              onChange={(value) => setFilter('Status', value ?? '')}
            />
            <Select
              placeholder="Batch"
              value={filters.ApplicationBatchId || undefined}
              allowClear
              options={batchOptions}
              loading={batchLoading}
              style={{ width: 150, borderColor: '#8F0024' }}
              onChange={(value) => setFilter('ApplicationBatchId', value ?? '')}
            />
            {/* <Select
              placeholder="Acceptance Uploaded"
              value={filters.HasUploadedAcceptance !== '' ? filters.HasUploadedAcceptance : undefined}
              allowClear
              options={[
                { label: 'Yes', value: true },
                { label: 'No', value: false },
              ]}
              style={{ width: 190, borderColor: '#8F0024' }}
              onChange={(value) => setFilter('HasUploadedAcceptance', value ?? '')}
            /> */}
            {/* <Select
              placeholder="Excl. Workflow Initiated"
              value={
                filters.ExcludeWorkflowInitiated !== ''
                  ? filters.ExcludeWorkflowInitiated
                  : undefined
              }
              allowClear
              options={[
                { label: 'Yes', value: true },
                { label: 'No', value: false },
              ]}
              style={{ width: 200, borderColor: '#8F0024' }}
              onChange={(value) => setFilter('ExcludeWorkflowInitiated', value ?? '')}
            /> */}
            <Button text="Clear Filters" variant="text" onClick={handleClearFilters} type="button" />
          </Flex>
        </div>

        <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
          <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>
            Showing <strong>{records.length}</strong> of <strong>{totalRecords}</strong> records
          </p>
        </Flex>

        <Table
          dataSource={records.map((r, i) => ({ ...r, key: `${r.applicantId}-${i}` }))}
          columns={columns}
          loading={isLoading}
          scroll={{ x: true }}
          pagination={{
            position: ['bottomCenter'],
            current: currentPage,
            total: totalRecords,
            onChange,
            pageSize: 10,
            showSizeChanger: false,
          }}
        />
      </section>
    </div>
  );
};

export default ScholarshipReport;
